import { ref, computed, onMounted } from 'vue';
import { resourcesService } from '@/api/resources.service';
import type { Praeparat } from '@/types/otc/praeparat';

/**
 * Verwaltet die Logik für einzelne Produktitems in der Suchergebnisliste
 * Zuständig für Labels, Produktdaten-Verarbeitung und Benutzerinteraktionen
 */
export function useSearchResultItem(
    props: { product: Praeparat, searchterm:string },
    emit: any
) {
    const dictionary = ref<Record<string, string>>({});
    const pflichttextUrl = ref('');

    /**
     * Lädt erforderliche Ressourcen beim Initialisieren
     */
    const loadResources = async () => {
        try {
            dictionary.value = await resourcesService.dictionaryArea('PRODUKTE');
            pflichttextUrl.value = await resourcesService.pflichttextUrl();
        } catch (error) {
            console.error('Fehler beim Laden der Ressourcen:', error);
        }
    };

    /**
     * Labels mit Fallback-Werten
     */
    const labelReadMore = computed(() =>
        dictionary.value['PRODUKTE_readMore'] ?? 'Mehr'
    );

    const labelOrder = computed(() =>
        dictionary.value['PRODUKTE_APOFINDER_ButtonApoBestellenShort'] ??
        dictionary.value['PRODUKTE_APOFINDER_ButtonApoBestellen'] ??
        'Bestellen'
    );

    const labelPflichttext = computed(() =>
        dictionary.value['PRODUKTE_Pflichttext'] ?? 'Pflichttext'
    );

    /**
     * Prüft ob das Produkt bestellbare Artikel hat
     */
    const hasArticles = computed(() =>
        props.product.artikel && props.product.artikel.length > 0
    );

    /**
     * Prüft ob das Produkt einen Pflichttext hat
     */
    const hasPflichttext = computed(() =>
        Boolean(props.product.wirkstoffInhalt)
    );

    /**
     * Erstellt gekürzte Produktbeschreibung
     */
    const shortDescription = computed(() => {
        if (!props.product.anwendungsgebietKurz) return '';

        const text = props.product.anwendungsgebietKurz;
        return text.length > 100 ? text.substring(0, 100) + '...' : text;
    });

    /**
     * Setzt Browser-History für Zurück-Navigation und navigiert zur Produktdetailseite
     */
    const onReadMore = () => {
        emit('readmore', props);
    };

    /**
     * Sammelt PZN-Nummern und leitet zur Apothekensuche weiter
     */
    const onOrder = computed(() => {
        if (!hasArticles.value) return;

        return props.product.artikel!.map(a => a.pzn).join(',');
    });

    /**
     * Öffnet Pflichttext-Seite und setzt History-Daten für Navigation
     */
    const onPflichttext = () => {
        if (!hasPflichttext.value) return;

        // setNavigationHistory();
        emit('pflichttext', props.product.produktId, props.product.id, props.searchterm);
    };

    /**
     * Setzt lokale Navigationsdaten für Zurück-Button
     */
    // const setNavigationHistory = () => {
    //     const historyKey = 'searchHistory';
    //     const currentUrl = window.location.toString();

    //     const getHistory = (): any => {
    //         const raw = localStorage.getItem(historyKey);
    //         return raw && raw !== 'undefined' ? JSON.parse(raw) : {};
    //     };

    //     const setHistory = (h: any) => localStorage.setItem(historyKey, JSON.stringify(h));

    //     const historyObj = getHistory();
    //     historyObj.psLocOrigin = historyObj.psLoc;
    //     historyObj.psLoc = currentUrl.replace(location.search, '');
    //     historyObj.product = props.product.produktId;
    //     historyObj.sip = true;
    //     setHistory(historyObj);
    // };

    onMounted(loadResources);

    return {
        dictionary,
        pflichttextUrl,
        labelReadMore,
        labelOrder,
        labelPflichttext,
        hasArticles,
        hasPflichttext,
        shortDescription,
        onReadMore,
        onOrder,
        onPflichttext
    };
}